import React, { useRef, useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  Avatar,
  Link,
  Stack,
  Chip,
  Grid,
  Paper,
  Tooltip,
} from "@mui/material";
import {
  AccessTime,
  Timer,
  Replay,
  Videocam,
  PlayCircle,
  InfoOutlined,
} from "@mui/icons-material";
import CameraPermissionScreen from "./camera-permission";
import { useLocation } from "react-router-dom";
import axios from "axios";

const VideoInterviewCard = () => {
  const videoRef = useRef(null);
  const [showInterviewScreen, setShowInterviewScreen] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [positions, setPositions] = useState([]);
  const [position, setPosition] = useState();
  const [candidate, setCandidate] = useState();

  const queryParams = new URLSearchParams(location.search);

  const urlId = queryParams.get("url_id");
  const positionId = queryParams.get("positionId");

  const [questions, setQuestions] = useState([]);

  useEffect(() => {
    const fetchQuestionsAndPosition = async () => {
      if (!positionId) return;
      try {
        // Fetch both position and questions data in one call
        const response = await axios.get(
          `https://api.a2ajobs.com/api/questions?position_id=${positionId}&url_id=${urlId}`
        );

        if (response.data.status === "success") {
          // Set the position data from the API response
          setPosition(response.data.data);

          // Set questions from the API response
          setQuestions(response.data.data.questions || []);

          // Set candidate if available
          setCandidate(response.data.candidate);
        }
      } catch (error) {
        console.error("Error fetching questions:", error);
      }
    };

    fetchQuestionsAndPosition();
  }, [positionId, urlId]);

  // Helper function to get video URL with proper formatting
  const getVideoUrl = (url) => {
    if (!url) return null;

    // Check if URL is properly formatted
    if (url.startsWith('https://') || url.startsWith('http://')) {
      return url;
    }

    // If it's a relative URL, prepend the base URL
    if (url.startsWith('/storage/')) {
      return `https://einterview.schoolmgmtsys.com${url}`;
    }

    return null;
  };

  const positionTitle = position?.title || "Default Job Title";

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "90vh",
        backgroundColor: "#f8f9fa",
        padding: { md: 0, xs: 1 },
      }}
    >
      {!showInterviewScreen ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "80vh",
            backgroundColor: "#f8f9fa",
            padding: { md: 2, xs: 0 },
          }}
        >
          <Card
            sx={{
              display: "flex",
              flexDirection: { xs: "column", md: "row" },
              maxWidth: 900,
              borderRadius: 3,
              boxShadow: 3,
              overflow: "hidden",
              height: "auto",
            }}
          >
            <Box
              sx={{
                width: { xs: "90%", md: "40%" },
                backgroundColor: "#0E1624",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 4,
                borderRadius: 3,
                boxShadow: "0 4px 12px rgba(0,0,0,0.4)",
              }}
            >
              <Avatar
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "#1666EB",
                  fontSize: 32,
                  mb: 2,
                }}
              >
                {candidate?.first_name?.charAt(0)?.toUpperCase() || "J"}
              </Avatar>

              <Typography
                variant="h6"
                sx={{ color: "white", fontWeight: 600, mb: 1 }}
              >
                {`${candidate?.first_name || "John"} ${candidate?.last_name || "Doe"
                  }`}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)", mb: 0.5 }}
              >
                {candidate?.email || "john.doe@example.com"}
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: "rgba(255,255,255,0.7)" }}
              >
                {candidate?.phone}
              </Typography>
            </Box>

            <Box
              sx={{
                width: { xs: "100%", md: "60%" },
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                padding: { md: 3, xs: 2 },
              }}
            >
              <Typography
                variant="caption"
                sx={{ fontWeight: "bold", color: "#7D7D7D" }}
              >
                VIDEO INTERVIEW
              </Typography>
              <Typography
                variant="h5"
                sx={{ fontWeight: "800", color: "#1666EB", mt: 1 }}
              >
                {positionTitle} - Axcel
              </Typography>
              <Typography variant="body2" sx={{ mt: 1, color: "#7D7D7D" }}>
                Before starting, watch the intro video to understand the
                process. Make sure you're in a quiet place with working
                equipment. You'll see the questions on the right. Start
                recording once, answer all questions, then stop and press
                Submit.
              </Typography>

              {/* Interview Configuration Cards */}
              {position && (
                <Paper
                  elevation={1}
                  sx={{
                    mt: 2,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1, display: "flex", alignItems: "center" }}>
                    Interview Configuration
                    <Tooltip title="These settings define how your interview will be conducted">
                      <InfoOutlined sx={{ fontSize: 16, ml: 0.5, color: "#7D7D7D" }} />
                    </Tooltip>
                  </Typography>
                  <Grid container spacing={1}>
                    <Grid item xs={6} md={4}>
                      <Tooltip title="Thinking Time: Time allowed to prepare your answer">
                        <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                          <AccessTime sx={{ fontSize: 18, color: "#1666EB", mr: 0.5 }} />
                          <Typography variant="caption" sx={{ fontWeight: "medium" }}>
                            {position.candidate_thinking_time || "15 Seconds"}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Tooltip title="Max Answer Length: Maximum duration for each answer">
                        <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                          <Timer sx={{ fontSize: 18, color: "#1666EB", mr: 0.5 }} />
                          <Typography variant="caption" sx={{ fontWeight: "medium" }}>
                            {position.candidate_max_answer_length || "2 Minutes"}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} md={4}>
                      <Tooltip title="Question Retakes: Number of attempts for each question">
                        <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }}>
                          <Replay sx={{ fontSize: 18, color: "#1666EB", mr: 0.5 }} />
                          <Typography variant="caption" sx={{ fontWeight: "medium" }}>
                            {position.question_retakes || "1 Retake"}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Button
                variant="contained"
                startIcon={<PlayCircle />}
                sx={{
                  mt: 3,
                  bgcolor: "#1666EB",
                  textTransform: "none",
                  fontSize: "16px",
                  mx: 1,
                }}
                onClick={() => setShowInterviewScreen(true)}
              >
                Watch Intro
              </Button>
            </Box>
          </Card>
        </Box>
      ) : (
        <Card
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            maxWidth: 900,
            borderRadius: 3,
            boxShadow: 3,
            overflow: "hidden",
            height: { xs: "auto", md: "500px" },
          }}
        >
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              backgroundColor: "#0E1624",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 0,
              minHeight: { xs: "200px", md: "auto" },
            }}
          >
            {getVideoUrl(position?.intro_video) && (
              <video
                src={getVideoUrl(position?.intro_video)}
                style={{
                  width: "100%",
                  maxWidth: "500px",
                  borderRadius: "8px",
                }}
                controls
                onError={(e) => {
                  console.error("Intro video failed to load:", e);
                  console.error("Video URL:", position?.intro_video);
                }}
              />
            )}
          </Box>
          <Box
            sx={{
              width: { xs: "100%", md: "50%" },
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              padding: { xs: 2, md: 3 },
              textAlign: { xs: "center", md: "left" },
            }}
          >
            <Typography
              variant="body1"
              sx={{
                fontWeight: "bold",
                color: "#7D7D7D",
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              VIDEO INTERVIEW
            </Typography>
            <Typography
              variant="h5"
              sx={{
                fontWeight: "800",
                color: "#1666EB",
                mt: 1,
                fontSize: { xs: "18px", md: "24px" },
              }}
            >
              {positionTitle} - Axcel
            </Typography>
            <Typography
              variant="body2"
              sx={{
                mt: 1,
                color: "#7D7D7D",
                fontSize: { xs: "12px", md: "14px" },
              }}
            >
              Welcome to your interview! After watching the introduction, click
              below to start answering the questions. Ensure your environment is
              ready.
            </Typography>

            {/* Interview Configuration Chips */}
            {position && (
              <Box sx={{ mt: 2, display: "flex", flexWrap: "wrap", gap: 1 }}>
                <Tooltip title="Thinking Time: Time allowed to prepare your answer">
                  <Chip
                    icon={<AccessTime />}
                    label={position.candidate_thinking_time || "15 Seconds"}
                    size="small"
                    variant="outlined"
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
                <Tooltip title="Max Answer Length: Maximum duration for each answer">
                  <Chip
                    icon={<Timer />}
                    label={position.candidate_max_answer_length || "2 Minutes"}
                    size="small"
                    variant="outlined"
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
                <Tooltip title="Question Retakes: Number of attempts for each question">
                  <Chip
                    icon={<Replay />}
                    label={position.question_retakes || "1 Retake"}
                    size="small"
                    variant="outlined"
                    sx={{ cursor: "pointer" }}
                  />
                </Tooltip>
              </Box>
            )}

            <Stack
              direction={"row"}
              gap={2}
              alignItems={"center"}
              justifyContent={{ xs: "center", md: "flex-start" }}
              mt={2}
            >
              <Button
                variant="contained"
                startIcon={<Videocam />}
                sx={{
                  bgcolor: "#1666EB",
                  textTransform: "none",
                  fontSize: { xs: "14px", md: "16px" },
                  padding: { xs: "6px 12px", md: "8px 16px" },
                }}
                onClick={() => setOpen(true)}
              >
                Start Interview
              </Button>
              <Stack color={"#7D7D7D"}>
                {questions?.length} questions
              </Stack>
            </Stack>
            <Typography
              variant="caption"
              sx={{
                mt: 2,
                color: "#7D7D7D",
                fontSize: { xs: "10px", md: "12px" },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              By clicking "Start Interview," you agree to our{" "}
              <Link>Terms and Conditions</Link>
            </Typography>
          </Box>
        </Card>
      )}
      <CameraPermissionScreen
        open={open}
        onClose={() => setOpen(false)}
        questions={questions}
        candidate={urlId}
        position={position}
        thinkingTime={position?.candidate_thinking_time}
        maxAnswerLength={position?.candidate_max_answer_length}
      />
    </Box>
  );
};

export default VideoInterviewCard;